import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

import { useNoaSpaceVisibilityManagementGuard } from '@/router/guards/noa-space-visibility-management.guard.js';
import { RouteNames } from '@/router/routeNames';
import { Routes } from '@/router/routes';
import { useAclMeta, useBreadcrumbMeta, useLayoutMeta } from '@/shared/layouts/base/composables';
import { UserType } from '@/shared/services/user/models';

import { useHomeGuard } from './guards/home.guard';
import { useVisibilityManagementGuard } from './guards/visibility-management.guard';

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: Routes.HOME,
      name: RouteNames.HOME,
      component: () => import('../pages/HomePage.vue'),
      meta: {
        ...useAclMeta(() => ({
          requireAuth: true,
        })),
      },
      beforeEnter: useHomeGuard,
    },
    {
      path: Routes.API_CLIENT,
      name: RouteNames.API_CLIENTS_PARENT,
      redirect: Routes.API_CLIENT,
      meta: {
        ...useAclMeta(() => ({
          requireAuth: true,
          allowedUserTypes: [UserType.CUSTOMER_SUPPORT],
        })),
      },
      children: [
        {
          path: '',
          name: RouteNames.API_CLIENTS,
          component: () => import('../pages/ApiClientsPage.vue'),
        },
        {
          path: 'new',
          name: RouteNames.NEW_API_CLIENT,
          component: () => import('../pages/AddApiClientPage.vue'),
          meta: {
            hideBreadcrumb: true,
          },
        },
        {
          path: ':id',
          meta: {
            ...useBreadcrumbMeta(({ t }) => ({
              label: t('consumers-list-title'),
              back: { name: RouteNames.API_CLIENTS },
            })),
          },
          children: [
            {
              path: '',
              name: RouteNames.API_CLIENT,
              component: () => import('../pages/ApiClientPage.vue'),
            },
            {
              path: 'edit',
              name: RouteNames.EDIT_API_CLIENT,
              component: () => import('../pages/EditApiClientPage.vue'),
              meta: {
                hideBreadcrumb: true,
              },
            },
            {
              path: 'facility',
              children: [
                {
                  path: 'new',
                  name: RouteNames.NEW_FACILITY,
                  component: () => import('../pages/AddFacilityPage.vue'),
                  meta: {
                    hideBreadcrumb: true,
                  },
                },
                {
                  path: ':facilityId',
                  meta: {
                    ...useBreadcrumbMeta((_, params) => ({
                      params: { apiClientId: params.id as string },
                      label: '',
                      isLoading: true,
                      back: { name: RouteNames.API_CLIENT, params: { id: params.id as string } },
                    })),
                  },
                  children: [
                    {
                      path: '',
                      name: RouteNames.FACILITY,
                      component: () => import('../pages/FacilityPage.vue'),
                    },
                    {
                      path: 'edit',
                      name: RouteNames.EDIT_FACILITY,
                      component: () => import('../pages/EditFacilityPage.vue'),
                      meta: {
                        hideBreadcrumb: true,
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: Routes.AUDIT_LOG,
      name: RouteNames.AUDIT_LOG,
      component: () => import('../pages/AuditLogPage.vue'),
      meta: {
        ...useAclMeta(() => ({
          requireAuth: true,
          allowedUserTypes: [UserType.CUSTOMER_SUPPORT],
        })),
      },
    },
    {
      path: Routes.VISIBILITY_MANAGEMENT,
      meta: {
        ...useAclMeta(() => ({
          requireAuth: true,
          allowedUserTypes: [UserType.FACILITY_ADMIN, UserType.CUSTOMER_SUPPORT],
        })),
      },
      children: [
        {
          path: '',
          name: RouteNames.VISIBILITY_MANAGEMENT,
          component: () => import('../pages/VisibilityManagementPage.vue'),
          beforeEnter: useVisibilityManagementGuard,
        },
        {
          path: 'api-clients/:id',
          children: [
            {
              path: 'facilities/:facilityId',
              name: RouteNames.VISIBILITY_MANAGEMENT_FACILITY,
              component: () => import('../pages/VisibilityManagementFacilityPage.vue'),
            },
          ],
        },
      ],
    },
    {
      path: Routes.NOA_SPACE,
      name: RouteNames.NOA_SPACE,
      meta: {
        ...useAclMeta(() => ({
          requireAuth: true,
          allowedUserTypes: [UserType.TECHNICAL_USER],
        })),
        beforeEnter: useNoaSpaceVisibilityManagementGuard,
      },
      children: [
        {
          path: '',
          name: RouteNames.NOA_SPACE_VISIBILITY_MANAGEMENT,
          component: () => import('../pages/VisibilityManagementPage.vue'),
        },
        {
          path: 'api-clients/:id',
          children: [
            {
              path: 'facilities/:facilityId',
              name: RouteNames.NOA_SPACE_VISIBILITY_MANAGEMENT_FACILITY,
              component: () => import('../pages/VisibilityManagementFacilityPage.vue'),
            },
          ],
        },
      ],
    },
    {
      path: Routes.LOGOUT,
      name: RouteNames.LOGOUT,
      component: () => import('../pages/LogoutPage.vue'),
      meta: {
        ...useAclMeta(() => ({
          requireAuth: false,
        })),
        ...useLayoutMeta(() => ({
          hideSidebar: true,
        })),
      },
    },
    {
      path: Routes.NOT_FOUND,
      name: RouteNames.NOT_FOUND,
      component: () => import('../pages/NotFoundPage.vue'),
    },
    {
      path: Routes.FORBIDDEN,
      name: RouteNames.FORBIDDEN,
      component: () => import('../pages/ForbiddenPage.vue'),
      meta: {
        ...useLayoutMeta(() => ({
          hideSidebar: true,
        })),
      },
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: Routes.NOT_FOUND,
    },
  ],
});

export const LOGIN_ROUTE = (): RouteRecordRaw => ({
  path: '/login',
  component: () => import('../pages/LoginPage.vue'),
  meta: {
    ...useLayoutMeta(() => ({
      hideSidebar: true,
    })),
    ...useAclMeta(() => ({
      requireAuth: false,
    })),
  },
});
